import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { itemService } from '../../services/itemService';
import { formatCurrency } from '../../utils/formatters';

const Skeleton: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <div
    style={{
      background: 'linear-gradient(90deg, rgba(239,232,213,0.6) 25%, rgba(92,78,51,0.2) 50%, rgba(239,232,213,0.6) 75%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.8s infinite',
      borderRadius: 3,
      ...style,
    }}
  />
);

export const ItemStats: React.FC = () => {
  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['item-stats'],
    queryFn: itemService.getItemStats
  });

  if (isLoading) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {[...Array(4)].map((_, i) => (
          <div key={i} style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: 20 }}>
            <Skeleton style={{ height: 9, width: '60%', marginBottom: 12 }} />
            <Skeleton style={{ height: 28, width: '80%' }} />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: 'rgba(201,90,90,0.08)', border: '1px solid rgba(201,90,90,0.2)', borderRadius: 6, padding: 16, marginBottom: 24 }}>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#c95a5a', margin: 0 }}>
          Failed to load item statistics
        </p>
      </div>
    );
  }

  const cards = [
    {
      title: 'Total Items',
      value: (stats?.totalItems ?? 0).toLocaleString(),
      accent: 'rgba(90,140,201,0.3)',
      iconColor: '#5a8cc9',
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      ),
    },
    {
      title: 'Active Items',
      value: (stats?.activeItems ?? 0).toLocaleString(),
      accent: 'rgba(90,201,140,0.3)',
      iconColor: '#5ac98c',
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      title: 'Low Stock Items',
      value: (stats?.lowStockItems ?? 0).toLocaleString(),
      accent: 'rgba(201,130,60,0.35)',
      iconColor: '#c9823c',
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        </svg>
      ),
    },
    {
      title: 'Total Value',
      value: formatCurrency(stats?.totalValue ?? 0),
      accent: 'rgba(217,119,6,0.35)',
      iconColor: '#d97706',
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
        </svg>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
      {cards.map((card, i) => (
        <div
          key={i}
          style={{
            background: '#ffffff',
            border: `1px solid ${card.accent}`,
            borderRadius: 6,
            padding: 20,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: 1,
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              background: `linear-gradient(90deg, transparent, ${card.accent}, transparent)`,
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 9,
                fontWeight: 500,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: '#6b6a5c',
              }}
            >
              {card.title}
            </div>
            <div style={{ color: card.iconColor, opacity: 0.8 }}>{card.icon}</div>
          </div>
          <div
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 28,
              fontWeight: 300,
              lineHeight: 1,
              background: 'linear-gradient(135deg, #d97706, #fbbf24)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {card.value}
          </div>
        </div>
      ))}
    </div>
  );
};
